import React from 'react';
import styled from 'styled-components';
import { format, utcToZonedTime } from 'date-fns-tz';
import { ColumnsType } from 'antd/es/table';
import { RadioGroup, RadioButton, Table, message, theme } from 'components';
import {
  getEmailDeliveryLog,
  getSmsDeliveryLog,
} from 'api/AppointmentController';
import { BuildJsonType } from '@bb/domain/dist/typeHelpers';
import { EnhancedAppointment, Timezone } from '@bb/domain/dist/types';
import getPushNotificationDeliveryLog from 'api/AppointmentController/getPushNotificationLog';
import { AppointmentDeliveryLog } from 'api/AppointmentController/getEmailDeliveryLog';
import { AppointmentSMSDeliveryLog } from 'api/AppointmentController/getSmsDeliveryLog';
import { StyledTable } from './HistorySection';

type LogsSectionProps = {
  appointment: BuildJsonType<EnhancedAppointment>;
  timezone: Timezone;
};

const getDefaultColumns = (
  timezone: Timezone,
): ColumnsType<AppointmentDeliveryLog | AppointmentSMSDeliveryLog> => [
  {
    key: 'recipient',
    title: 'Recipient',
    dataIndex: 'recipient',
  },
  {
    key: 'eventType',
    title: 'Event Type',
    dataIndex: 'eventType',
  },
  {
    key: 'status',
    title: 'Status',
    dataIndex: 'status',
  },
  {
    key: 'time',
    title: 'Time',
    dataIndex: 'time',
    render: time =>
      format(utcToZonedTime(time, timezone), 'MMM dd, yyyy hh:mm:ss aa'),
  },
];

const mobileNumColumn = {
  key: 'mobileNumber',
  title: 'Mobile Number',
  dataIndex: 'mobileNumber',
};

const LogsSection: React.FC<LogsSectionProps> = ({ appointment, timezone }) => {
  const defaultColumns = getDefaultColumns(timezone);

  const [loading, setLoading] = React.useState(false);
  const [tab, setTab] = React.useState('email');
  const [emptyTabText, setEmptyTabText] = React.useState('email delivery');
  const [columns, setColumns] = React.useState(defaultColumns);
  const [data, setData] = React.useState<
    (AppointmentDeliveryLog | AppointmentSMSDeliveryLog)[]
  >([]);

  React.useEffect(() => {
    const fetchLogData = async () => {
      let fn;
      if (tab === 'email') {
        fn = getEmailDeliveryLog;
        setEmptyTabText('email delivery');
      } else if (tab === 'sms') {
        fn = getSmsDeliveryLog;
        setEmptyTabText('SMS delivery');
      } else {
        fn = getPushNotificationDeliveryLog;
        setEmptyTabText('push notification');
      }

      try {
        setLoading(true);
        const _data = await fn(appointment.id);
        setData(_data);
      } catch (err) {
        message.error('Failed to fetch appointment delivery logs');
      } finally {
        if (tab === 'sms') {
          setColumns([
            defaultColumns[0],
            mobileNumColumn,
            ...defaultColumns.slice(1),
          ]);
        } else {
          setColumns(defaultColumns);
        }
        setLoading(false);
      }
    };

    fetchLogData();
  }, [tab]);

  const onChange = (e: any) => setTab(e.target.value);

  return (
    <>
      <StyledRadioGroup onChange={onChange} value={tab}>
        <StyledRadioButton value="email">Email</StyledRadioButton>
        <StyledRadioButton value="sms">SMS</StyledRadioButton>
        <StyledRadioButton value="push">Push Notification</StyledRadioButton>
      </StyledRadioGroup>
      <StyledTable
        dataSource={data}
        columns={columns}
        loading={loading}
        pagination={{
          size: 'small',
          position: ['bottomLeft'],
          total: data.length,
          pageSize: 5,
        }}
        locale={{
          emptyText: `There are no ${emptyTabText} logs for this showing request.`,
        }}
      />
    </>
  );
};

const StyledRadioButton = styled(RadioButton)`
  && {
    border-radius: 0;
  }
`;

const StyledRadioGroup = styled(RadioGroup)`
  margin: 16px 0;
`;

export default LogsSection;
